/**
 * Semantic presets for `TextClamp` and related primitives.
 *
 * Numeric clamp budgets (`minLines`, `maxLines`, `maxChars`) come only from JSON
 * Schema `x-limits.text` via {@link visibleTextClampFromSchema} in `./text-limits`.
 * Overflow *strategy* (line-clamp vs slice vs truncate) is documented in `./text-overflow-policy`.
 * `lineHeight` is presentation-only and layered on here.
 *
 * Cover meta grid (dt/dd) uses `coverMetaLabel` / `coverMetaValue` from the
 * cover schema. Logo line and generic info-box chrome remain local where no schema slot exists.
 *
 * @see DECK_SYSTEM_AUDIT.md Section 3.1 "Visual Slot Consistency"
 */
import {
  SLIDE_HEADER_TEXT_REFERENCE_SLIDE_TYPE,
  visibleTextClampFromSchema,
} from "./text-limits";

export type TextConstraint = {
  minLines?: number;
  maxLines?: number;
  lineHeight?: number | string;
  /** Maximum characters before truncation (schema-backed when using visibleTextClampFromSchema) */
  maxChars?: number;
};

/**
 * Reference slide types for shared components that do not yet receive `slideType`
 * from the caller (CT-AUDIT-021B/021C).
 */
const REF = {
  execSummary: "exec-summary",
  choiceBars: "single-select-bars",
} as const;

export const TEXT_CONSTRAINTS = {
  // Slide header elements
  slideTitle: visibleTextClampFromSchema(
    "title",
    SLIDE_HEADER_TEXT_REFERENCE_SLIDE_TYPE,
    {
      lineHeight: 1,
    },
  ),
  slideEyebrow: visibleTextClampFromSchema(
    "eyebrow",
    SLIDE_HEADER_TEXT_REFERENCE_SLIDE_TYPE,
    {
      lineHeight: 1.1,
    },
  ),
  slideActionTitle: visibleTextClampFromSchema("actionTitle", REF.execSummary, {
    lineHeight: 1.2,
  }),
  slideActionSubtitle: visibleTextClampFromSchema(
    "actionSubtitle",
    REF.execSummary,
    {
      lineHeight: 1.2,
    },
  ),

  // Cover slide elements
  coverEyebrow: visibleTextClampFromSchema("eyebrow", "cover", {
    lineHeight: 1.1,
  }),
  coverSubtitle: visibleTextClampFromSchema("subtitle", "cover", {
    lineHeight: 1.5,
  }),
  // Sumana clips descenders at line-height 1 in the browser even though Figma uses 1em.
  // Keep the adjustment title-only so the cover layout stays visually the same.
  coverTitle: visibleTextClampFromSchema("title", "cover", {
    lineHeight: 1.06,
  }),
  coverLogoText: { minLines: 1, maxLines: 1, lineHeight: 1, maxChars: 40 },
  coverMetaLine: visibleTextClampFromSchema("coverMetaLabel", "cover", {
    lineHeight: 1.6,
  }),
  coverMeta: visibleTextClampFromSchema("coverMetaValue", "cover", {
    lineHeight: 1.4,
  }),
  coverHeroTitle: visibleTextClampFromSchema("title", "cover", {
    lineHeight: 1.06,
  }),
  coverHeroSubtitle: visibleTextClampFromSchema("subtitle", "cover", {
    lineHeight: 1.5,
  }),

  coverPill: visibleTextClampFromSchema("tag", "cover", { lineHeight: 1.4 }),
  coverFooter: visibleTextClampFromSchema("footerText", "cover", {
    lineHeight: "var(--ds-lh-normal)",
  }),

  // Quote elements
  insightQuote: visibleTextClampFromSchema("quoteText", REF.choiceBars, {
    lineHeight: 1.4,
  }),
  quoteSource: visibleTextClampFromSchema("quoteText", REF.choiceBars, {
    lineHeight: 1.2,
  }),
} as const;

/**
 * Get text constraints for a specific element type.
 * Returns undefined if not found.
 */
export function getTextConstraint(
  key: keyof typeof TEXT_CONSTRAINTS,
): TextConstraint | undefined {
  return TEXT_CONSTRAINTS[key];
}

/**
 * Get the max characters for a text element.
 * Falls back to 500 if not specified.
 */
export function getMaxChars(key: keyof typeof TEXT_CONSTRAINTS): number {
  return TEXT_CONSTRAINTS[key]?.maxChars ?? 500;
}
